import { useCallback, useEffect, useState, type MouseEvent, type RefObject } from "react";
import { cn } from "@components/utils/utils";

interface ShowcaseTocEntry {
  id: string;
  title: string;
}

interface ShowcaseSidebarProps {
  containerRef: RefObject<HTMLElement>;
  refreshKey: string;
}

const extractTitleText = (section: HTMLElement) => {
  const heading = section.querySelector("h3");
  if (!heading) return "";
  const firstNode = heading.firstChild;
  return firstNode?.textContent?.trim() ?? heading.textContent?.trim() ?? "";
};

const sameEntries = (a: ShowcaseTocEntry[], b: ShowcaseTocEntry[]) =>
  a.length === b.length && a.every((entry, idx) => entry.id === b[idx]?.id && entry.title === b[idx]?.title);

export const ShowcaseSidebar = ({ containerRef, refreshKey }: ShowcaseSidebarProps) => {
  const [entries, setEntries] = useState<ShowcaseTocEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const collect = () => {
      const container = containerRef.current;
      if (!container) return;
      const sections = container.querySelectorAll<HTMLElement>("section[id]");
      const next: ShowcaseTocEntry[] = [];
      sections.forEach(section => {
        const title = extractTitleText(section);
        if (title) next.push({ id: section.id, title });
      });
      setEntries(previous => (sameEntries(previous, next) ? previous : next));
    };
    collect();
    const raf = window.requestAnimationFrame(collect);
    return () => window.cancelAnimationFrame(raf);
  }, [containerRef, refreshKey]);

  useEffect(() => {
    if (entries.length === 0) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      observed => {
        const visible = observed.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -55% 0px", threshold: 0 },
    );
    entries.forEach(entry => {
      const target = document.getElementById(entry.id);
      if (target) observer.observe(target);
    });
    return () => observer.disconnect();
  }, [entries]);

  const handleClick = useCallback((event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActiveId(id);
  }, []);

  if (entries.length === 0) return null;

  return (
    <nav aria-label="Sommaire du design system" className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto pr-2">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sur cette page</p>
      <ul className="space-y-0.5 text-sm">
        {entries.map(entry => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              onClick={event => handleClick(event, entry.id)}
              className={cn(
                "block border-l-2 py-1 pl-3 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                activeId === entry.id ? "border-foreground font-medium text-foreground" : "border-border text-muted-foreground",
              )}
            >
              {entry.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};
